import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SeoHead from '@/components/feature/SeoHead';
import { allArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = useMemo(() => {
    return allArticles.find((a) => a.slug === slug);
  }, [slug]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return allArticles
      .filter((a) => a.id !== article.id && a.category === article.category)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <main>
        <Navbar />
        <div className="pt-16 md:pt-18 min-h-[400px] flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
              <i className="ri-article-line text-foreground-400 text-2xl"></i>
            </div>
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-2">文章未找到</h2>
            <p className="text-foreground-500 text-sm mb-4">您访问的文章不存在或已被移除</p>
            <Link
              to="/category/all?tab=articles"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              返回资讯列表
            </Link>
          </div>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main>
      <SeoHead
        title={`${article.title} - 九游娱乐`}
        description={article.excerpt}
        keywords={`九游娱乐,${article.category},${article.title},游戏资讯`}
        canonicalUrl={`https://www.jiuyou.com/article/${article.slug}`}
        ogType="article"
        schemaType="Article"
        articleData={{
          headline: article.title,
          datePublished: article.date,
          dateModified: article.date,
          author: article.author,
          imageUrl: article.image,
          category: article.category,
        }}
      />
      <Navbar />

      <article className="pt-16 md:pt-18">
        <div className="w-full bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 lg:px-8 py-8 md:py-10">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-4 flex-wrap" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-foreground-400"></i>
              <Link to="/category/all?tab=articles" className="hover:text-primary-600 transition-colors cursor-pointer">最新资讯</Link>
              <i className="ri-arrow-right-s-line text-foreground-400"></i>
              <span className="text-foreground-800 font-medium">{article.category}</span>
              <i className="ri-arrow-right-s-line text-foreground-400"></i>
              <span className="text-foreground-800 font-medium line-clamp-1">{article.title}</span>
            </nav>
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  '@context': 'https://schema.org',
                  '@type': 'BreadcrumbList',
                  itemListElement: [
                    { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.jiuyou.com/' },
                    { '@type': 'ListItem', position: 2, name: '最新资讯', item: 'https://www.jiuyou.com/category/all?tab=articles' },
                    { '@type': 'ListItem', position: 3, name: article.category, item: `https://www.jiuyou.com/article/${article.slug}` },
                    { '@type': 'ListItem', position: 4, name: article.title, item: `https://www.jiuyou.com/article/${article.slug}` },
                  ],
                }),
              }}
            />

            <div className="inline-block mb-3">
              <span className="px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
                {article.category}
              </span>
            </div>

            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 md:gap-6 text-foreground-500 text-xs md:text-sm">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                {(article.views / 10000).toFixed(1)}万阅读
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                阅读{article.readTime}
              </span>
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 lg:px-8 py-10 md:py-12 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8 md:mb-10 rounded-xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-56 md:h-80 object-cover object-top"
              />
            </div>

            <div
              className="prose-content text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-foreground-500 text-xs">标签：</span>
                <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs cursor-pointer hover:bg-secondary-200 transition-colors">九游娱乐</span>
                <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs cursor-pointer hover:bg-secondary-200 transition-colors">{article.category}</span>
                <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs cursor-pointer hover:bg-secondary-200 transition-colors">游戏攻略</span>
              </div>
            </div>
          </div>
        </div>

        {relatedArticles.length > 0 && (
          <div className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-14 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-6">相关推荐</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
                {relatedArticles.map((item) => (
                  <Link
                    key={item.id}
                    to={`/article/${item.slug}`}
                    className="group rounded-xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-300 transition-all duration-200 cursor-pointer"
                  >
                    <div className="relative h-36 overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="font-heading font-semibold text-foreground-900 text-sm line-clamp-2 group-hover:text-primary-600 transition-colors mb-2">
                        {item.title}
                      </h4>
                      <div className="flex items-center gap-3 text-foreground-500 text-xs">
                        <span>{item.date}</span>
                        <span>{item.readTime}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </article>

      <Footer />
      <ScrollToTop />
    </main>
  );
}